import cloudflareAssets from "@/assets/cloudflareAssets";
import type { IAccountTransaction } from "@/modules/account/common/types";
import { useGetAccountTransactionsQuery } from "@/modules/account/services";
import { INITIAL_PAGE, PageSizeOptions } from "@/shared/common/constants";
import {
  CloudflareAsset,
  Currencies,
  ErrorMessages,
  HiddenColumns,
  TableNames
} from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, FilterPanel, Select } from "@/shared/components";
import { getErrorMessage, getTableName } from "@/shared/helpers";
import { useFilters, useTableHooks } from "@/shared/hooks";
import {
  AdvancedTable as Table,
  ColumnSettings
} from "@ab.uitools/ui-library/components/AdvancedTable";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { getColumns } from "./columns";

export const AccountHistory = () => {
  const { accountId = "" } = useParams();
  const { t } = useTranslation();
  const { filters, setFilters, defaultFilters } = useFilters();

  const [accountHistory, setAccountHistory] = useState<IAccountTransaction[]>([]);

  const {
    page,
    pageSize,
    tableRef,
    hasNextPage,
    onNextPage,
    onPrevPage,
    setHasNextPage,
    onPageSizeChange,
    onPageIndexReset,
    updateHasNextPage,
  } = useTableHooks<IAccountTransaction>();

  const {
    data: list,
    isFetching,
    isError,
    error,
  } = useGetAccountTransactionsQuery(
    {
      accountId: accountId,
      pageIndex: page,
      pageSize,
      ...filters,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const currency = list?.[0]?.currency;

  const columns = getColumns({ t });

  const defaultHiddenColumns = useMemo(() => {
    if (currency && currency === Currencies.AMD) {
      return [HiddenColumns.EquivalentInAMD];
    }
    return [];
  }, [currency]);

  useEffect(() => {
    if (!list) return;

    if (list?.length || (!list?.length && page === INITIAL_PAGE)) {
      setAccountHistory(list);
      updateHasNextPage(list.length);
    } else if (!list?.length && page !== INITIAL_PAGE) {
      onPrevPage();
      setHasNextPage(false);
    }
  }, [list]);

  const tableName = getTableName(TableNames.AccountHistory);

  return (
    <div className="position-relative page-body">
      <div className="full-width flexbox justify-content--center">
        <div
          className={classNames("advanced_table-wrapper", {
            "table-full": accountHistory.length,
            "table-empty": !accountHistory.length,
          })}>
          <div className="full-width pb-24">
            <Heading className="content-title" weight="bolder" size="xsmall">
              {t("accounts.accountHistory")}
            </Heading>
          </div>
          <Table
            data={accountHistory || []}
            withSelect
            columns={columns}
            defaultHiddenColumns={defaultHiddenColumns}
            isLoading={isFetching || !tableName}
            hasError={isError}
            tableSettings={{
              tableName: tableName || "",
              persistColumnSettings: "localStorage"
            }}
            emptyTitle={
              isError ?
                t(getErrorMessage(error as IError, ErrorMessages.TryAgainShortly)) :
                t("general.noData")
            }
            emptySubTitle={
              !isError ? t("general.noDataAvailable") : ""
            }
            emptyIllustration={cloudflareAssets[CloudflareAsset.EmptyContent]}
            renderHeader={(table) => (
              <div className="advanced-table__header flexbox justify-content--end py-16 px-24">
                <FilterPanel
                  filters={filters}
                  setFilters={setFilters}
                  defaultFilters={defaultFilters}
                  resetPagination={onPageIndexReset}
                />
                <div className="flexbox">
                  <ColumnSettings
                    table={table}
                    allToggleText={t("general.all")}
                    hiddenColumns={defaultHiddenColumns}
                    tooltipText={t("general.columnCantBeHidden")}
                  />
                </div>
              </div>
            )}
            renderFooter={(table) => {
              tableRef.current = table;
              return (
                <div className="advanced-table__footer">
                  <div className="advanced-table__pagination">
                    <Select
                      setSelectedValue={(value) => onPageSizeChange(value as string)}
                      selectedValue={`${pageSize}`}
                      options={PageSizeOptions}
                    />
                    <div className="flexbox align-items--center">
                      <Button
                        onClick={onPrevPage}
                        size="medium"
                        type="tertiary"
                        data-testid="account-history-prev-page"
                        data-ga-id="account-history-prev-page"
                        data-ga-action="Account history table previos page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortLeft}/>
                          ),
                        }}
                        disabled={page === INITIAL_PAGE}
                      />
                      <Button
                        onClick={onNextPage}
                        size="medium"
                        type="tertiary"
                        data-testid="account-history-next-page"
                        data-ga-id="account-history-next-page"
                        data-ga-action="Account history table next page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortRight}/>
                          ),
                        }}
                        disabled={!hasNextPage}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};
